/* eslint-disable @typescript-eslint/restrict-template-expressions */
import type { UserPlanContribution, FundsNameType } from "pages/Orders/PlanWiseContribution";
import type { UserContribution, PlansNameType, PensionPlansType } from "pages/Orders/UserWiseContribution";
import type { TodayOrderType, orderConfirmationMessage, orderConfirmationStatus } from '../pages/Orders/TodayOrder';
import type { OrderHistoryInterface } from '../pages/Orders/OrderHistory';
import type { OrderStatusInterface } from '../pages/Orders/OrderHistory/OrderStatus';
import ApiUtils from "utils/ApiUtil";
import { ordersApiUrls } from "constants/apiEndPoints";
import { type OrderFilterType } from "pages/Orders/OrderDetailed";
import { strings } from "i18n/Strings";
import { type OrderDetailsAttributesType } from "types/services";
import { transactionTypes } from "constants/constantText";
import QueryParamsBuilder from "components/helpers/commonFunctions/QueryParamsBuilder";
import type { UnitsReallocationFilterType } from "pages/UnitsReallocation";


const getAllUserWiseContribution = async (page: number, size: number, sortBy: string,
    sortOrder: string, searchValue: string): Promise<UserContribution[]> => {
    const url = QueryParamsBuilder({ baseUrl: ordersApiUrls.getAllUserWiseContributionUrl, page, size, sortBy, sortOrder, searchValue });
    return await ApiUtils.getWithToken(url) as UserContribution[];
}

const getAllUserPlanWiseContribution = async (): Promise<UserPlanContribution[]> => {
    return await ApiUtils.getWithToken(ordersApiUrls.getAllUserPlanWiseContributionUrl) as UserPlanContribution[];
}

const getAllTodayOrderList = async (page: number, size: number): Promise<TodayOrderType[]> => {
    return await ApiUtils.getWithToken(`${ordersApiUrls.getAllTodayOrderListUrl}?page=${page}&size=${size}`) as TodayOrderType[];
}

const getContributtedAmount = async (): Promise<unknown> => {
    return await ApiUtils.getWithToken(`${ordersApiUrls.getTotalContributtedAmount}`);
}

const getAllOrderList = async (page: number, size: number, sortBy: string,
    sortOrder: string, filters: OrderFilterType): Promise<OrderHistoryInterface[]> => {
    try {
        let url = QueryParamsBuilder({ baseUrl: ordersApiUrls.getAllOrderListUrl, page, size, sortBy, sortOrder, searchValue: '' });

        const filterParams = new URLSearchParams();
        if (filters) {
            if (filters.startDate) {
                filterParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                filterParams.append('endDate', filters.endDate);
            }
            if (filters.status) {
                const statusMap = {
                    [strings.displayText.filledLabel]: strings.displayText.filledLabel,
                    [strings.displayText.pendingLabel]: strings.displayText.pendingLabel,
                    [strings.displayText.submittedLabel]: strings.displayText.submittedLabel,
                    [strings.displayText.cancelled]: strings.displayText.cancelled,
                };

                const status = statusMap[filters.status] || strings.displayText.all;
                filterParams.append('status', status);
            }
            if (filters.orderType) {
                filterParams.append('orderType',
                    filters.orderType === strings.displayText.buy
                        ? strings.displayText.buy : filters.orderType === strings.displayText.sell
                            ? strings.displayText.sell : strings.displayText.all);
            }
        }

        if (filterParams.toString()) {
            url += `&${filterParams.toString()}`;
        }
        const data = await ApiUtils.getWithToken(url) as OrderHistoryInterface[];
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

const createAndPlaceOrder = async (data: TodayOrderType): Promise<TodayOrderType[]> => {
    const assignedList = await ApiUtils.postWithToken(ordersApiUrls.createAndPlaceOrderUrl, { contractId: data.contractId, totalContribution: data.totalContribution, orderMethod: data?.transactionMethod, data }) as TodayOrderType[];
    return assignedList;
};


const getAllPlans = async (): Promise<PlansNameType[]> => {
    return await ApiUtils.getWithToken(ordersApiUrls.getAllPlansUrl) as PlansNameType[]
};

const getAllPensionPlans = async (): Promise<PensionPlansType[]> => {
    return await ApiUtils.getWithToken(ordersApiUrls.getAllPensionPlans) as PensionPlansType[]
};

const getAllFunds = async (): Promise<FundsNameType[]> => {
    return await ApiUtils.getWithToken(ordersApiUrls.getAllFundsUrl) as FundsNameType[]

};

const getIBOrderStatus = async (data: number): Promise<OrderStatusInterface> => {
    const assignedList = await ApiUtils.getWithToken(`${ordersApiUrls.getIBOrderStatusUrl}${data}`);
    return (assignedList as { result: OrderStatusInterface }).result

};

const getOrderById = async (data: number): Promise<OrderStatusInterface> => {
    const assignedList = await ApiUtils.getWithToken(`${ordersApiUrls.getIbOrderById}${data}`) as OrderStatusInterface;
    return assignedList;

};

const confirmationIBorder = async (data: orderConfirmationStatus): Promise<orderConfirmationMessage> => {
    const assignedList = await ApiUtils.postWithToken(`${ordersApiUrls.confirmIBOrderReplyUrl}${data.orderId}`, { confirmation: data.confirmation, data });
    return (assignedList as { result: orderConfirmationMessage }).result;
};

const createAndPlaceSalesOrder = async (data: TodayOrderType): Promise<TodayOrderType[]> => {
    const assignedList = await ApiUtils.postWithToken(ordersApiUrls.createAndPlaceOrderUrl, {
        contractId: data.contractId, units: data.units, orderMethod: data?.orderMethod, data: {
            ...data
        }
    }) as TodayOrderType[];
    return assignedList;
};

const displayUserUnitsPerOrder = async (orderId: number): Promise<unknown> => {
    return await ApiUtils.getWithToken(`order/user-units/${orderId}`);
}
const getAllMMFundSalesList = async (page: number, size: number, sortBy: string, sortOrder: string, searchValue: string, filters: unknown): Promise<unknown> => {
    let url = QueryParamsBuilder({ baseUrl: ordersApiUrls.getAllMMUnitSaleList, page, size, sortBy, sortOrder, searchValue });
    const filterParams = new URLSearchParams();

    if (filters.status) {
        const statusMap = {
            [strings.displayText.filledLabel]: strings.displayText.filledLabel,
            [strings.displayText.pendingLabel]: strings.displayText.pendingLabel,
            [strings.displayText.submittedLabel]: strings.displayText.submittedLabel,
        };

        const status = statusMap[filters.status] || '';
        filterParams.append('status', status);
    }
    if (filterParams.toString()) {
        url += `&${filterParams.toString()}`;
    }
    return await ApiUtils.getWithToken(url);
}

const getEmployeeDetailsByEmailOrUniqueId = async (searchValue: string): Promise<unknown> => {
    try {

        const res = await ApiUtils.getWithToken(`${ordersApiUrls.getEmployeeDetailsByEmailOrUniqueIdUrl}/${searchValue}`);
        return res
    } catch (error) {
        console.error('Error fetching organization details:', error);
        throw error;
    }
};


const transferOutUserUnits = async (uniqueId: string | undefined, transferValues: object): Promise<unknown> => {
    try {

        const res = await ApiUtils.postWithToken(`${ordersApiUrls.transferOutUserUnits}`, { uniqueId, transferValues });
        return res
    } catch (error) {
        console.error('Error updating units transfer out:', error);
        throw error?.response?.data?.message;
    }
};

const getAllUsersOrderByDetail = async (
    page: number,
    size: number,
    sortBy: string,
    sortOrder: string,
    filters: OrderFilterType,
): Promise<OrderDetailsAttributesType[]> => {
    try {
        let url = QueryParamsBuilder({ baseUrl: ordersApiUrls.getAllUsersOrderByDetail, page, size, sortBy, sortOrder });

        const filterParams = new URLSearchParams();
        if (filters) {
            if (filters.startDate) {
                filterParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                filterParams.append('endDate', filters.endDate);
            }
            if (filters.status) {
                const statusMap = {
                    [strings.displayText.filledLabel]: strings.displayText.filledLabel,
                    [strings.displayText.pendingLabel]: strings.displayText.pendingLabel,
                    [strings.displayText.submittedLabel]: strings.displayText.submittedLabel,
                    [strings.displayText.cancelledLabel]: strings.displayText.cancelledLabel
                };

                const status = statusMap[filters.status] || strings.displayText.all;
                filterParams.append('status', status);
            }
            if (filters.orderType) {
                filterParams.append('orderType',
                    filters.orderType === strings.displayText.buy
                        ? strings.displayText.buy : filters.orderType === strings.displayText.sell
                            ? strings.displayText.sell : strings.displayText.all);
            }
            if (filters.employerId) {
                filterParams.append('employerId', filters.employerId);
            }
        }

        if (filterParams.toString()) {
            url += `&${filterParams.toString()}`;
        }
        const data = await ApiUtils.getWithToken(url) as TodayOrderType[];
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

const getAllUsersOrderTotalByDetail = async (
    filters: OrderFilterType,
): Promise<OrderDetailsAttributesType[]> => {
    try {
        let url = `${ordersApiUrls.getAllUsersOrderTotalByDetail}`;

        const filterParams = new URLSearchParams();
        if (filters) {
            if (filters.startDate) {
                filterParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                filterParams.append('endDate', filters.endDate);
            }
            if (filters.status) {
                const statusMap = {
                    [strings.displayText.filledLabel]: strings.displayText.filledLabel,
                    [strings.displayText.pendingLabel]: strings.displayText.pendingLabel,
                    [strings.displayText.submittedLabel]: strings.displayText.submittedLabel,
                    [strings.displayText.cancelledLabel]: strings.displayText.cancelledLabel
                };

                const status = statusMap[filters.status] || strings.displayText.all;
                filterParams.append('status', status);
            }
            if (filters.orderType) {
                filterParams.append('orderType',
                    filters.orderType === strings.displayText.buy
                        ? strings.displayText.buy : filters.orderType === strings.displayText.sell
                            ? strings.displayText.sell : strings.displayText.all);
            }
            if (filters.employerId) {
                filterParams.append('employerId', filters.employerId);
            }
        }

        if (filterParams.toString()) {
            url += `?${filterParams.toString()}`;
        }

        const data = await ApiUtils.getWithToken(url) as OrderDetailsAttributesType[];
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

const exportOrdersInDetails = async (
    filters: OrderFilterType,
): Promise<unknown> =>
    await new Promise((resolve, reject) => {
        let url = `${ordersApiUrls.exportOrdersInDetails}`;
        const filterParams = new URLSearchParams();
        if (filters) {
            if (filters.startDate) {
                filterParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                filterParams.append('endDate', filters.endDate);
            }
            if (filters.status) {
                const statusMap = {
                    [strings.displayText.filledLabel]: strings.displayText.filledLabel,
                    [strings.displayText.pendingLabel]: strings.displayText.pendingLabel,
                    [strings.displayText.submittedLabel]: strings.displayText.submittedLabel,
                };

                const status = statusMap[filters.status] || strings.displayText.all;

                filterParams.append('status', status);
            }
            if (filters.orderType) {
                filterParams.append('orderType',
                    filters.orderType === strings.displayText.buy
                        ? strings.displayText.buy : filters.orderType === strings.displayText.sell
                            ? strings.displayText.sell : strings.displayText.all);
            }
            if (filters.employerId) {
                filterParams.append('employerId', filters.employerId);
            }
        }

        if (filterParams.toString()) {
            url += `?${filterParams.toString()}`;
        }

        ApiUtils.getExcelFile(url)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((res: any) => {
                const blob = new Blob([res.data], { type: res.headers['content-type'] });
                const url = URL.createObjectURL(blob);
                resolve(url);
            })
            .catch((error) => {
                reject(error);
            });
    });

const exportAllOrderHistoryList = async (
    filters: OrderFilterType,
): Promise<unknown> =>
    await new Promise((resolve, reject) => {
        let url = `${ordersApiUrls.exportOrderHistory}`;
        const filterParams = new URLSearchParams();
        if (filters) {
            if (filters.startDate) {
                filterParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                filterParams.append('endDate', filters.endDate);
            }
            if (filters.status) {
                const statusMap = {
                    [strings.displayText.filledLabel]: strings.displayText.filledLabel,
                    [strings.displayText.pendingLabel]: strings.displayText.pendingLabel,
                    [strings.displayText.submittedLabel]: strings.displayText.submittedLabel,
                };

                const status = statusMap[filters.status] || strings.displayText.all;

                filterParams.append('status', status);
            }
            if (filters.orderType) {
                filterParams.append('orderType',
                    filters.orderType === strings.displayText.buy
                        ? strings.displayText.buy : filters.orderType === strings.displayText.sell
                            ? strings.displayText.sell : strings.displayText.all);
            }
        }

        if (filterParams.toString()) {
            url += `?${filterParams.toString()}`;
        }

        ApiUtils.getExcelFile(url)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((res: any) => {
                const blob = new Blob([res.data], { type: res.headers['content-type'] });
                const url = URL.createObjectURL(blob);
                resolve(url);
            })
            .catch((error) => {
                reject(error);
            });
    });

const exportOrdersReport = async (orderType: string, date: string): Promise<unknown> => await new Promise((resolve, reject) => {
    let url = `${ordersApiUrls.exportOrderReport}`
    const filterParams = new URLSearchParams();

    if (date) {
        filterParams.append("date", date);
    }
    if (orderType === transactionTypes.buy) {
        filterParams.append("orderType", "BUY");
    } else {
        filterParams.append("orderType", "SELL");

    }
    if (filterParams.toString()) {
        url += `?${filterParams.toString()}`
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ApiUtils.getExcelFile(url).then((res: any) => {
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = URL.createObjectURL(blob);
        resolve(url);
    }).catch((error) => {
        reject(error);
    })
})

const getMasterFundsList = async (): Promise<TodayOrderType[]> => {
    return await ApiUtils.getWithToken(`${ordersApiUrls.getMasterFundsList}`) as TodayOrderType[];
}

const addBuyOrderValue = async (data: TodayOrderType): Promise<TodayOrderType[]> => {
    const result = await ApiUtils.postWithToken(ordersApiUrls.zpUserBuyValue, { payload: data }) as TodayOrderType[];
    return result;
};

const getAllTodayOrderListUsersDetailed = async (page: number, size: number, fundId: number): Promise<TodayOrderType[]> => {
    return await ApiUtils.getWithToken(`${ordersApiUrls.getAllTodayOrderListUsersDetailed}?page=${page}&size=${size}&fundId=${fundId}`) as TodayOrderType[];
}

const updateUnitReallocation = async (
    data: { userId: number; fundId: number; unitsAllocated: number; reAllocatedDate: Date },
): Promise<unknown> => {
    try {
        const response = await ApiUtils.postWithToken(
            ordersApiUrls.updateUnitReallocation,
            { payload: { data } }
        );
        return response;
    } catch (error) {
        console.error('Error updating units for reallocation:', error);
        throw error;
    }
};

const getAllReallocationData = async (
    page: number,
    size: number,
    sortBy: string,
    sortOrder: string,
    filters: UnitsReallocationFilterType,
): Promise<unknown> => {
    try {
        let url = QueryParamsBuilder({ baseUrl: ordersApiUrls.getAllReallocationData, page, size, sortBy, sortOrder, searchValue: '' });
        const filterParams = new URLSearchParams();

        if (filters) {
            if (filters.startDate) {
                filterParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                filterParams.append('endDate', filters.endDate);
            }
        }

        if (filterParams.toString()) {
            url += `&${filterParams.toString()}`;
        }

        const data = await ApiUtils.getWithToken(url) as UnitsReallocationFilterType[];
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

const exportAllReallocationData = async (filters: UnitsReallocationFilterType,): Promise<unknown> => await new Promise((resolve, reject) => {
    let url = QueryParamsBuilder({ baseUrl: ordersApiUrls.exportAllReallocationData });
    const filterParams = new URLSearchParams();

    if (filters) {
        if (filters.startDate) {
            filterParams.append('startDate', filters.startDate);
        }
        if (filters.endDate) {
            filterParams.append('endDate', filters.endDate);
        }
    }

    if (filterParams.toString()) {
        url += `&${filterParams.toString()}`;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ApiUtils.getExcelFile(url).then((res: any) => {
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = URL.createObjectURL(blob);
        resolve(url);
    }).catch((error) => {
        reject(error);
    })
})

export {
    getContributtedAmount,
    getAllUserWiseContribution,
    getAllUserPlanWiseContribution,
    getAllTodayOrderList,
    createAndPlaceOrder,
    getAllOrderList,
    getAllPlans,
    getAllFunds,
    getIBOrderStatus,
    confirmationIBorder,
    getOrderById,
    createAndPlaceSalesOrder,
    displayUserUnitsPerOrder,
    getAllMMFundSalesList,
    getEmployeeDetailsByEmailOrUniqueId,
    transferOutUserUnits,
    getAllUsersOrderByDetail,
    getAllUsersOrderTotalByDetail,
    exportOrdersInDetails,
    getAllPensionPlans,
    exportAllOrderHistoryList,
    exportOrdersReport,
    getMasterFundsList,
    addBuyOrderValue,
    getAllTodayOrderListUsersDetailed,
    updateUnitReallocation,
    getAllReallocationData,
    exportAllReallocationData
}